// W2Cook Billing Dashboard — Persistence layer
//
// In development (VITE_USE_FIRESTORE=false): uses localStorage.
// In production  (VITE_USE_FIRESTORE=true):  uses Firestore via REST API.
//
// Firestore REST requires a Firebase service-account ID token or
// user-auth token set as VITE_FIRESTORE_TOKEN.
// Collection: `billing-dashboard/{docId}`

import { useState, useEffect, useCallback } from "react";

const USE_FIRESTORE = import.meta.env.VITE_USE_FIRESTORE === "true";
const FIRESTORE_PROJECT = import.meta.env.VITE_FIREBASE_PROJECT_ID ?? "";
const FIRESTORE_TOKEN   = import.meta.env.VITE_FIRESTORE_TOKEN ?? "";

const FIRESTORE_BASE =
  `https://firestore.googleapis.com/v1/projects/${FIRESTORE_PROJECT}/databases/(default)/documents/billing-dashboard`;

// ── Firestore REST helpers ────────────────────────────────────────────────────

function toFirestoreValue(val: unknown): unknown {
  if (val === null)             return { nullValue: null };
  if (typeof val === "boolean") return { booleanValue: val };
  if (typeof val === "number")  return { doubleValue: val };
  if (typeof val === "string")  return { stringValue: val };
  if (Array.isArray(val))       return { arrayValue: { values: val.map(toFirestoreValue) } };
  if (typeof val === "object")  return { mapValue: { fields: Object.fromEntries(Object.entries(val as object).map(([k, v]) => [k, toFirestoreValue(v)])) } };
  return { stringValue: String(val) };
}

function fromFirestoreValue(fv: any): unknown {
  if (fv.nullValue    !== undefined) return null;
  if (fv.booleanValue !== undefined) return fv.booleanValue;
  if (fv.doubleValue  !== undefined) return fv.doubleValue;
  if (fv.integerValue !== undefined) return Number(fv.integerValue);
  if (fv.stringValue  !== undefined) return fv.stringValue;
  if (fv.arrayValue)  return (fv.arrayValue.values ?? []).map(fromFirestoreValue);
  if (fv.mapValue)    return Object.fromEntries(Object.entries(fv.mapValue.fields ?? {}).map(([k, v]) => [k, fromFirestoreValue(v)]));
  return null;
}

async function fsGet<T>(docId: string): Promise<T | null> {
  const res = await fetch(`${FIRESTORE_BASE}/${docId}`, {
    headers: { Authorization: `Bearer ${FIRESTORE_TOKEN}` },
  });
  if (!res.ok) return null;
  const doc = await res.json();
  if (!doc.fields?.data) return null;
  return fromFirestoreValue(doc.fields.data) as T;
}

async function fsSet<T>(docId: string, value: T): Promise<void> {
  await fetch(`${FIRESTORE_BASE}/${docId}?updateMask.fieldPaths=data`, {
    method: "PATCH",
    headers: {
      Authorization: `Bearer ${FIRESTORE_TOKEN}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ fields: { data: toFirestoreValue(value) } }),
  });
}

// ── localStorage helpers ──────────────────────────────────────────────────────

function lsGet<T>(key: string, fallback: T): T {
  try { const v = localStorage.getItem(key); return v ? JSON.parse(v) : fallback; }
  catch { return fallback; }
}

function lsSet<T>(key: string, val: T): void {
  try { localStorage.setItem(key, JSON.stringify(val)); } catch {}
}

// ── Public hook ───────────────────────────────────────────────────────────────

export function useStore<T>(key: string, defaultValue: T) {
  const [value, setValue] = useState<T>(() => lsGet(key, defaultValue));
  const [ready, setReady] = useState(!USE_FIRESTORE);

  // Hydrate from Firestore on mount
  useEffect(() => {
    if (!USE_FIRESTORE) return;
    fsGet<T>(key).then((remote) => {
      if (remote !== null) setValue(remote);
      setReady(true);
    });
  }, [key]);

  const set = useCallback((updater: T | ((prev: T) => T)) => {
    setValue((prev) => {
      const next = typeof updater === "function" ? (updater as (p: T) => T)(prev) : updater;
      if (USE_FIRESTORE) {
        fsSet(key, next).catch(console.error);
      } else {
        lsSet(key, next);
      }
      return next;
    });
  }, [key]);

  return [value, set, ready] as const;
}
